import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';
import useGlobalContext from '@susu/custom-made/providers/hooks/useGlobalContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import { applyBusinessRulesForFabricSelection } from '@susu/custom-made/utils/businessRules';
import type {
  BusinessRules,
  DefaultValues,
  Fabric,
  OptionConfigurationType,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import type { ProductType } from '@susu/custom-made/utils/productDefinition';
import { useCallback } from 'react';

const { SHOW_LOGS } = FEATURE_TOGGLES;

export const useApplyFabricBusinessRules = ({
  businessRules,
  options,
  optionValues,
  defaultValues,
  product,
  isInPreloadFlow,
}: {
  businessRules: BusinessRules;
  options: OptionConfigurationType;
  optionValues: typeOptionValue;
  defaultValues: DefaultValues;
  product: ProductType | undefined;
  isInPreloadFlow: boolean;
}) => {
  const { isManualSelectionDone } = useGlobalContext();
  const { selectedOptions, hiddenOptionsAndValues, selectedBaseStyle, updateHiddenOptionsAndValues, selectOption } =
    useOptionContext();
  const showLogsFeatureEnabled = useFeatureIsOn(SHOW_LOGS);

  return useCallback(
    (selectedFabric: Fabric, prevFabric?: Fabric) => {
      if (!selectedFabric || !defaultValues || !optionValues || !options || !businessRules || !product) {
        return;
      }

      const updatedValues = applyBusinessRulesForFabricSelection(
        structuredClone(selectedOptions),
        businessRules,
        structuredClone(hiddenOptionsAndValues),
        optionValues,
        selectedFabric.id,
        options,
        defaultValues,
        product,
        isManualSelectionDone,
        selectedBaseStyle,
        showLogsFeatureEnabled,
        prevFabric?.id,
        isInPreloadFlow,
      );

      updateHiddenOptionsAndValues(updatedValues.hiddenOptionsAndValues);
      selectOption(updatedValues.selectedOptions);

      window.DD_RUM?.addAction('Custom made - Fabric Clicked', {
        customData: {
          previousSelectedFabric: prevFabric,
          previousSelectedOptions: selectedOptions,
          previousHiddenOptionsAndValues: hiddenOptionsAndValues,
          selectedFabricTmp: selectedFabric,
          selectedOptions: updatedValues.selectedOptions,
          hiddenOptionsAndValues: updatedValues.hiddenOptionsAndValues,
        },
      });
    },
    [
      businessRules,
      options,
      optionValues,
      defaultValues,
      product,
      isInPreloadFlow,
      isManualSelectionDone,
      selectedOptions,
      hiddenOptionsAndValues,
      selectedBaseStyle,
      updateHiddenOptionsAndValues,
      selectOption,
      showLogsFeatureEnabled,
    ],
  );
};
